import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  INSTALLATION_CREATE_SECTIONS,
  INSTALLATION_VIEW_SECTIONS,
  INSTALLATION_EDIT_SECTIONS,
} from "./docs/installation/installationTestCases";
import {
  REALTOR_INSTALLATION_CREATE_SECTIONS,
  REALTOR_INSTALLATION_VIEW_SECTIONS,
} from "./docs/realtor-installation/realtorInstallationTestCases";
import {
  AGENT_INSTALLATION_CREATE_SECTIONS,
  AGENT_INSTALLATION_VIEW_SECTIONS,
  AGENT_INSTALLATION_EDIT_SECTIONS,
} from "./docs/agent-installation/agentInstallationTestCases";

const STORAGE_KEY = "orders_testing_center_checks";
const THEME_STORAGE_KEY = "orders_testing_center_theme";
const SECTIONS_STORAGE_KEY = "orders_testing_center_sections";

type ThemeMode = "dark" | "light";

// ─── Theme (developer / modern UX) ────────────────────────────────────────
type Theme = {
  bg: { page: string; card: string; cardHover: string; sidebar: string; content: string };
  accent: string;
  accentMuted: string;
  success: string;
  text: { primary: string; secondary: string; muted: string };
  border: string;
  radius: { sm: number; md: number; lg: number };
  fontMono: string;
};

const themeDark: Theme = {
  bg: { page: "#0f172a", card: "#1e293b", cardHover: "#334155", sidebar: "#0f172a", content: "#0f172a" },
  accent: "#22d3ee",
  accentMuted: "rgba(34, 211, 238, 0.15)",
  success: "#34d399",
  text: { primary: "#f8fafc", secondary: "#94a3b8", muted: "#64748b" },
  border: "rgba(148, 163, 184, 0.2)",
  radius: { sm: 6, md: 10, lg: 14 },
  fontMono: "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace",
};

const themeLight: Theme = {
  bg: { page: "#f1f5f9", card: "#ffffff", cardHover: "#f8fafc", sidebar: "#e2e8f0", content: "#f1f5f9" },
  accent: "#0891b2",
  accentMuted: "rgba(8, 145, 178, 0.12)",
  success: "#059669",
  text: { primary: "#0f172a", secondary: "#475569", muted: "#64748b" },
  border: "rgba(148, 163, 184, 0.35)",
  radius: { sm: 6, md: 10, lg: 14 },
  fontMono: "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace",
};

type TabId = "create" | "view" | "edit" | "agent-checkout" | "realtor-checkout" | "routes" | "installation-create" | "installation-view" | "installation-edit" | "realtor-installation-create" | "realtor-installation-view" | "agent-installation-create" | "agent-installation-view" | "agent-installation-edit";

type Section = { sectionId?: string; title: string; icon: string; items: { id: string; label: string }[] };
type SectionsState = Record<string, Section[]>;

// ─── Create Order test cases (from CreateOrder-TestCases.md) ─────────────────
const CREATE_SECTIONS: { title: string; icon: string; items: { id: string; label: string }[] }[] = [
  {
    title: "1. Pre-submit validation (UI)",
    icon: "ri-error-warning-line",
    items: [
      { id: "C-V01", label: "No billing or shipping address → error shown" },
      { id: "C-V02", label: "No products → Add at least one product error" },
      { id: "C-V03", label: "Product quantity zero or negative → quantity greater than 0 error" },
      { id: "C-V04", label: "Can proceed only with address + products + shipping + required fees" },
    ],
  },
  {
    title: "2. Buyer type and selection (UI)",
    icon: "ri-user-settings-line",
    items: [
      { id: "C-B01", label: "Agent flow: customer details and address from agent" },
      { id: "C-B02", label: "Brokerage flow — office/team member required" },
      { id: "C-B03", label: "Brokerage flow — optional agent switches to agent details" },
      { id: "C-B04", label: "Cascade reset: change brokerage clears office, team member, agent" },
      { id: "C-B05", label: "Cascade reset: change office clears team member, agent" },
    ],
  },
  {
    title: "3. Billing and shipping address (UI)",
    icon: "ri-map-pin-line",
    items: [
      { id: "C-A01", label: "Same as billing: only billing dropdown; shipping uses billing" },
      { id: "C-A02", label: "Different addresses: both dropdowns shown and required" },
      { id: "C-A03", label: "Add address: new address in list and selected" },
      { id: "C-A04", label: "Shipping for rates: correct address for shipping options/tax" },
    ],
  },
  {
    title: "4. Product table (UI)",
    icon: "ri-shopping-cart-line",
    items: [
      { id: "C-P01", label: "Add product: row with quantity 1, unit price from variant/effective" },
      { id: "C-P02", label: "Quantity change: effective price from API; manual cleared; tax recalc" },
      { id: "C-P03", label: "Manual unit price: overrides effective/original; total and tax recalc" },
      { id: "C-P04", label: "Remove product: row removed; totals and discount recalc" },
      { id: "C-P05", label: "Quantity validation: reject negative or e; no qty ≤ 0 on submit" },
      { id: "C-P06", label: "Price priority: manual cleared on qty change; effective used" },
    ],
  },
  {
    title: "5. Shipping options (UI + payload)",
    icon: "ri-truck-line",
    items: [
      { id: "C-S01", label: "Select manual shipping: shipPayloadState has row(s); amount and tax" },
      { id: "C-S02", label: "Select UPS shipping: rates from address/dimensions" },
      { id: "C-S03", label: "Edit shipping amount: custom manual entry; tax recalc" },
      { id: "C-S04", label: "No shipping option: Save blocked by canProceedToSave" },
      { id: "C-S05", label: "is_customer_pickup true in payload when shipping-costs has it" },
      { id: "C-S06", label: "is_customer_pickup false in payload when not customer pickup" },
      { id: "C-S07", label: "Multiple shipping rows: each has is_customer_pickup from shippingCosts" },
    ],
  },
  {
    title: "6. Special fees (UI)",
    icon: "ri-price-tag-3-line",
    items: [
      { id: "C-F01", label: "Required fee: blocked until all required fees selected" },
      { id: "C-F02", label: "Select optional fee: amount and tax in totals and payload" },
      { id: "C-F03", label: "Fee reset on buyer change (effectiveCouponCreatedById)" },
    ],
  },
  {
    title: "7. Tax (UI)",
    icon: "ri-calculator-line",
    items: [
      { id: "C-T01", label: "Product tax: TaxEngineUniversal; per-line and total shown" },
      { id: "C-T02", label: "Shipping tax in totals and shipping row(s)" },
      { id: "C-T03", label: "Special fee tax in totals" },
      { id: "C-T04", label: "Tax address: same as billing vs different shipping" },
    ],
  },
  {
    title: "8. Coupon / discount (UI)",
    icon: "ri-coupon-line",
    items: [
      { id: "C-D01", label: "Apply percentage coupon: discount and per-item allocation" },
      { id: "C-D02", label: "Apply fixed amount coupon: allocated across items" },
      { id: "C-D03", label: "Eligibility: cart below min_cart_value not applied or error" },
      { id: "C-D04", label: "Discount display: Discount (CODE): -$X.XX in order totals" },
      { id: "C-D05", label: "Add/remove product with fixed discount: re-allocated" },
    ],
  },
  {
    title: "9. Order totals (UI)",
    icon: "ri-file-list-3-line",
    items: [
      { id: "C-O01", label: "Subtotal: sum(unit price × qty) before discount" },
      { id: "C-O02", label: "After discount: subtotal, discount line, tax, grand total" },
      { id: "C-O03", label: "Shipping and fees in totals; grand total includes all" },
      { id: "C-O04", label: "Busy state: totals/buttons respect; no double submit" },
    ],
  },
  {
    title: "10. Submit and payload (API)",
    icon: "ri-send-plane-line",
    items: [
      { id: "C-X01", label: "Successful create: POST /orders; success message; redirect" },
      { id: "C-X02", label: "order_charges has shipping_option and specialfee" },
      { id: "C-X03", label: "is_customer_pickup per row in order_charges[0].shipping_option" },
      { id: "C-X04", label: "Draft unique ID obtained/cached; cleared on success" },
      { id: "C-X05", label: "Submit error: message shown; form stays editable" },
      { id: "C-X06", label: "Items payload: item_tax_info, unit_retail_price, discount_on_total_quantity, etc." },
    ],
  },
  {
    title: "11. UI display and accessibility",
    icon: "ri-palette-line",
    items: [
      { id: "C-U01", label: "Save button: enabled when canProceedToSave; disabled when submitting" },
      { id: "C-U02", label: "Form error display visible (e.g. above form or near Save)" },
      { id: "C-U03", label: "Route: /signage/createOrder vs /signageteammember/createOrder context" },
    ],
  },
  {
    title: "12. Summary checklist (Create Order)",
    icon: "ri-checkbox-multiple-line",
    items: [
      { id: "C-SUM1", label: "All validation errors show correctly (address, products, qty, shipping, required fees)" },
      { id: "C-SUM2", label: "Buyer flows: Agent; Brokerage → Office → Team Member; optional Agent; cascade resets" },
      { id: "C-SUM3", label: "Address: same-as-billing, different addresses, add new address" },
      { id: "C-SUM4", label: "Products: add, remove, quantity, manual price, effective price, tax and totals" },
      { id: "C-SUM5", label: "Shipping: manual, UPS, edit amount; each row in payload has is_customer_pickup from shipping-costs" },
      { id: "C-SUM6", label: "Special fees: required/optional selection, reset on buyer change" },
      { id: "C-SUM7", label: "Tax: product, shipping, special fee; correct address used" },
      { id: "C-SUM8", label: "Coupon: percentage/fixed, eligibility, display, re-allocation" },
      { id: "C-SUM9", label: "Submit: POST payload includes order_charges[0].shipping_option[].is_customer_pickup; success redirect; error handling" },
    ],
  },
];

// ─── View Order test cases (from ViewOrder-TestCases.md) ────────────────────
const VIEW_SECTIONS: { title: string; icon: string; items: { id: string; label: string }[] }[] = [
  {
    title: "1. Display and data (UI)",
    icon: "ri-file-list-3-line",
    items: [
      { id: "V-D01", label: "Order details load: header, customer, addresses, items, charges, totals" },
      { id: "V-D02", label: "order_charges display: shipping_option and specialfee rows shown" },
      { id: "V-D03", label: "Totals from order_charges match order" },
      { id: "V-D04", label: "Billing address section correct (heading style per design)" },
      { id: "V-D05", label: "Shipping address section correct" },
      { id: "V-D06", label: "Customer details: name, email, phone (#eff6ff heading etc.)" },
    ],
  },
  {
    title: "2. Shipped vs Ready To Pickup (UI)",
    icon: "ri-truck-line",
    items: [
      { id: "V-S01", label: "Shipped button when NOT customer pickup (truck icon, label Shipped)" },
      { id: "V-S02", label: "Ready To Pickup when any row has is_customer_pickup (bag-check, label Ready To Pickup)" },
      { id: "V-S03", label: "Ready To Pickup click still calls onOrderStatus(301)" },
      { id: "V-S04", label: "Source of truth: any row is_customer_pickup → show Ready To Pickup" },
      { id: "V-S05", label: "Visibility: status 101 and payment_info present" },
    ],
  },
  {
    title: "3. Status actions (UI)",
    icon: "ri-checkbox-circle-line",
    items: [
      { id: "V-A01", label: "Confirm (100 → 101) working" },
      { id: "V-A02", label: "Shipped / Ready To Pickup (101 → 301) working" },
      { id: "V-A03", label: "Mark as Delivered (301 → 302) working" },
      { id: "V-A04", label: "Signage-specific actions shown when applicable" },
    ],
  },
  {
    title: "4. Order totals and charges (UI)",
    icon: "ri-money-dollar-circle-line",
    items: [
      { id: "V-O01", label: "Payment Details heading style (e.g. #eff6ff bg)" },
      { id: "V-O02", label: "Order Totals heading style" },
      { id: "V-O03", label: "Order Notes heading style" },
      { id: "V-O04", label: "Shipping from order_charges displayed in totals" },
      { id: "V-O05", label: "Special fees from order_charges displayed" },
    ],
  },
  {
    title: "5. Integration with Create/Edit payload",
    icon: "ri-links-line",
    items: [
      { id: "V-I01", label: "Create with customer pickup → View shows Ready To Pickup" },
      { id: "V-I02", label: "Edit with customer pickup → View shows Ready To Pickup" },
    ],
  },
  {
    title: "6. Summary checklist (View Order)",
    icon: "ri-checkbox-multiple-line",
    items: [
      { id: "V-SUM1", label: "Order details load; addresses, items, and charges displayed correctly" },
      { id: "V-SUM2", label: "When any order_charges.shipping_option row has is_customer_pickup: true: show Ready To Pickup with bag-check icon; otherwise Shipped with truck icon" },
      { id: "V-SUM3", label: "Shipped/Ready To Pickup only when status 101 and paid; click still triggers status 301" },
      { id: "V-SUM4", label: "Confirm, Mark as Delivered, and other status actions work and respect route/context" },
      { id: "V-SUM5", label: "Headings (Customer Details, Billing, Shipping, Payment Details, Order Totals, Order Notes) have required UI treatment (#eff6ff where specified)" },
      { id: "V-SUM6", label: "Create/Edit payloads with is_customer_pickup display correctly on view" },
    ],
  },
];

// ─── Edit Order test cases (from EditOrder-TestCases.md) ────────────────────
const EDIT_SECTIONS: { title: string; icon: string; items: { id: string; label: string }[] }[] = [
  {
    title: "1. Load and initialization (UI)",
    icon: "ri-download-cloud-line",
    items: [
      { id: "E-L01", label: "Load order: form and sections populated from order and order_charges" },
      { id: "E-L02", label: "Products from order.items; prices from unit_retail_price/effectiveMap/manualUnitPriceMap" },
      { id: "E-L03", label: "Addresses set; same as billing reflects both_address_same" },
      { id: "E-L04", label: "Shipping from order_charges[0].shipping_option; selected IDs set" },
      { id: "E-L05", label: "Special fees from order_charges[0].specialfee" },
      { id: "E-L06", label: "Discount from order; userAppliedCoupon false initially" },
      { id: "E-L07", label: "Saved totals on load (not live engine totals)" },
      { id: "E-L08", label: "Hydration guard: busy states do not disable Save during load" },
    ],
  },
  {
    title: "2. Status-based editability (UI)",
    icon: "ri-lock-line",
    items: [
      { id: "E-E01", label: "Status 100 (Draft): all fields editable" },
      { id: "E-E02", label: "Status 101: BillingAddress, ShippingAddress, ShippingMethod, Items, Notes" },
      { id: "E-E03", label: "Status 200/201: same subset editable" },
      { id: "E-E04", label: "Status 202: ShippingAddress, Notes, Items, ShippingMethod" },
      { id: "E-E05", label: "Status 300/301/302: only Notes editable" },
      { id: "E-E06", label: "Status 400 (Canceled): no fields editable" },
      { id: "E-E07", label: "UI reflects editability per isFieldEditable(status, field)" },
    ],
  },
  {
    title: "3. Saved vs live totals (UI)",
    icon: "ri-refresh-line",
    items: [
      { id: "E-T01", label: "Initial display: tax, discount, subtotal, grand total = saved" },
      { id: "E-T02", label: "Switch to live after edit (useLiveTotals true)" },
      { id: "E-T03", label: "Saved vs live tax: saved first, then live after edit" },
      { id: "E-T04", label: "Saved vs live discount: after apply/remove coupon shows live" },
      { id: "E-T05", label: "Discount code in submit: existing vs new vs empty" },
    ],
  },
  {
    title: "4. Products (UI)",
    icon: "ri-shopping-cart-line",
    items: [
      { id: "E-P01", label: "Edit quantity: effective price fetched; manual cleared; recalc" },
      { id: "E-P02", label: "Manual unit price: stored; totals and tax recalc" },
      { id: "E-P03", label: "Add product: new row; effective price; tax and discount recalc" },
      { id: "E-P04", label: "Remove product: totals and discount re-allocation recalc" },
      { id: "E-P05", label: "Items disabled when status does not allow Items edit" },
    ],
  },
  {
    title: "5. Shipping (UI + payload)",
    icon: "ri-truck-line",
    items: [
      { id: "E-S01", label: "Edit shipping amount: row updated; tax recalc; markUserEdited" },
      { id: "E-S02", label: "is_customer_pickup true in payload for matching shipping-costs row" },
      { id: "E-S03", label: "is_customer_pickup false when not customer pickup" },
      { id: "E-S04", label: "Shipping disabled when ShippingMethod not editable" },
    ],
  },
  {
    title: "6. Tax recalculation and TaxCloud",
    icon: "ri-calculator-line",
    items: [
      { id: "E-TC01", label: "tax_status draft: tax recalc; no refund; PATCH with new tax" },
      { id: "E-TC02", label: "tax_status complete: allowRefund; refund; PATCH; POST taxcloud_order/version" },
      { id: "E-TC03", label: "Zero out tax after refund where backend recalcs" },
      { id: "E-TC04", label: "Recalculate before submit on Save" },
    ],
  },
  {
    title: "7. Validation and submit (UI + API)",
    icon: "ri-send-plane-line",
    items: [
      { id: "E-V01", label: "Quantity > 0: error if any product quantity 0" },
      { id: "E-V02", label: "Unit price > 0: error if 0 or negative" },
      { id: "E-X01", label: "Successful update: PATCH orders-update/:orderId; redirect" },
      { id: "E-X02", label: "order_charges in payload with is_customer_pickup per shipping row" },
      { id: "E-X03", label: "Submit error: message shown; form stays editable" },
    ],
  },
  {
    title: "8. UI display and routes",
    icon: "ri-palette-line",
    items: [
      { id: "E-U01", label: "Save button: enabled when validation passes; disabled when submitting" },
      { id: "E-U02", label: "Route: /signage/editOrder vs /signageteammember/editOrder" },
      { id: "E-U03", label: "Version/order number displayed when applicable" },
    ],
  },
  {
    title: "9. Summary checklist (Edit Order)",
    icon: "ri-checkbox-multiple-line",
    items: [
      { id: "E-SUM1", label: "Order loads; all sections and saved totals correct; hydration does not disable Save" },
      { id: "E-SUM2", label: "Editability per status: Draft (all), 101/200/201/202 (subset), 300/301/302 (Notes only), 400 (none)" },
      { id: "E-SUM3", label: "Saved totals on load; live totals after first user edit; discount code in submit correct" },
      { id: "E-SUM4", label: "Products: add, remove, quantity, manual price; disabled when Items not editable" },
      { id: "E-SUM5", label: "Shipping: edit amount when editable; payload has is_customer_pickup per row from shipping-costs" },
      { id: "E-SUM6", label: "Tax: recalc on edit; TaxCloud refund when tax_status complete; version creation after refund" },
      { id: "E-SUM7", label: "Validation and PATCH payload; redirect on success; error handling" },
    ],
  },
];

// ─── Agent Order Checkout test cases (from AgentOrderCheckout-TestCases.md) ─
const AGENT_CHECKOUT_SECTIONS: { title: string; icon: string; items: { id: string; label: string }[] }[] = [
  {
    title: "1. Validation (UI)",
    icon: "ri-error-warning-line",
    items: [
      { id: "AC-V01", label: "Name required: Formik validation" },
      { id: "AC-V02", label: "Billing address required (hasBilling)" },
      { id: "AC-V03", label: "Shipping address required (hasShipping)" },
      { id: "AC-V04", label: "Shipping option required (hasShippingOption)" },
      { id: "AC-V05", label: "Required special fees: all must be selected" },
      { id: "AC-V06", label: "Cart not empty" },
    ],
  },
  {
    title: "2. Address (UI)",
    icon: "ri-map-pin-line",
    items: [
      { id: "AC-A01", label: "Same as billing: shipping uses billing" },
      { id: "AC-A02", label: "Different addresses: both required" },
      { id: "AC-A03", label: "Add address: new address in list and selected" },
      { id: "AC-A04", label: "Address for shipping/tax correct" },
    ],
  },
  {
    title: "3. Cart / products (UI)",
    icon: "ri-shopping-cart-line",
    items: [
      { id: "AC-C01", label: "Cart from context: items and prices correct" },
      { id: "AC-C02", label: "Quantity change: totals and tax recalc" },
      { id: "AC-C03", label: "Remove item: totals and tax recalc" },
    ],
  },
  {
    title: "4. Shipping options (UI + payload)",
    icon: "ri-truck-line",
    items: [
      { id: "AC-S01", label: "Select manual shipping: shipPayload.shipping_option; amount and tax" },
      { id: "AC-S02", label: "Select UPS shipping: rates from address/dimensions" },
      { id: "AC-S03", label: "Edit shipping amount if allowed: custom entry; tax recalc" },
      { id: "AC-S04", label: "order_charges in payload: shipping_option correct" },
    ],
  },
  {
    title: "5. Special fees (UI)",
    icon: "ri-price-tag-3-line",
    items: [
      { id: "AC-F01", label: "Required fee: amount and tax in totals and payload" },
      { id: "AC-F02", label: "Optional fee: amount and tax in totals and payload" },
    ],
  },
  {
    title: "6. Tax (UI)",
    icon: "ri-calculator-line",
    items: [
      { id: "AC-T01", label: "Product tax: TaxEngineUniversal; per-line and total" },
      { id: "AC-T02", label: "Shipping tax in totals" },
      { id: "AC-T03", label: "Special fee tax in totals" },
    ],
  },
  {
    title: "7. Coupon / discount (UI)",
    icon: "ri-coupon-line",
    items: [
      { id: "AC-D01", label: "Apply coupon: valid coupon; cart meets min" },
      { id: "AC-D02", label: "Eligibility: cart below min_cart_value" },
      { id: "AC-D03", label: "Expiration: expired coupon not applied" },
    ],
  },
  {
    title: "8. Payment (UI)",
    icon: "ri-bank-card-line",
    items: [
      { id: "AC-P01", label: "Payment method selection: Stripe or manual" },
      { id: "AC-P02", label: "Stripe payment: complete flow; order can place" },
    ],
  },
  {
    title: "9. Submit and payload (API)",
    icon: "ri-send-plane-line",
    items: [
      { id: "AC-X01", label: "Successful place order: order_charges; success; redirect or clear cart" },
      { id: "AC-X02", label: "order_charges structure: shipping_option and specialfee" },
      { id: "AC-X03", label: "Submit error: message shown; form stays editable" },
    ],
  },
  {
    title: "10. Summary checklist (Agent Checkout)",
    icon: "ri-checkbox-multiple-line",
    items: [
      { id: "AC-SUM1", label: "Validation: name, billing, shipping, shipping option, required fees" },
      { id: "AC-SUM2", label: "Address: same-as-billing, different, add address" },
      { id: "AC-SUM3", label: "Cart: items from context; totals and tax" },
      { id: "AC-SUM4", label: "Shipping: manual/UPS; order_charges.shipping_option in payload" },
      { id: "AC-SUM5", label: "Special fees: required/optional in payload" },
      { id: "AC-SUM6", label: "Tax: product, shipping, special fee" },
      { id: "AC-SUM7", label: "Coupon: apply, eligibility, expiration" },
      { id: "AC-SUM8", label: "Payment: method selection; Stripe or manual" },
      { id: "AC-SUM9", label: "Submit: success redirect; error handling" },
    ],
  },
];

// ─── Realtor Order Checkout test cases (from RealtorOrderCheckout-TestCases.md) ─
const REALTOR_CHECKOUT_SECTIONS: { title: string; icon: string; items: { id: string; label: string }[] }[] = [
  {
    title: "1. Validation (UI)",
    icon: "ri-error-warning-line",
    items: [
      { id: "RC-V01", label: "Name required" },
      { id: "RC-V02", label: "Office required when team member" },
      { id: "RC-V03", label: "Team member required when team member" },
      { id: "RC-V04", label: "Agent required when agent" },
      { id: "RC-V05", label: "Billing address required" },
      { id: "RC-V06", label: "Shipping address required" },
      { id: "RC-V07", label: "Shipping option required" },
      { id: "RC-V08", label: "Required special fees: all must be selected" },
      { id: "RC-V09", label: "Cart not empty" },
    ],
  },
  {
    title: "2. User type and selection (UI)",
    icon: "ri-user-settings-line",
    items: [
      { id: "RC-U01", label: "Agent flow: select agent; addresses from agent" },
      { id: "RC-U02", label: "Team member flow: office then team member required" },
      { id: "RC-U03", label: "Cascade: change office clears team member" },
    ],
  },
  {
    title: "3. Address (UI)",
    icon: "ri-map-pin-line",
    items: [
      { id: "RC-A01", label: "Same as billing: shipping uses billing" },
      { id: "RC-A02", label: "Different addresses: both required" },
      { id: "RC-A03", label: "Add address: new address in list and selected" },
      { id: "RC-A04", label: "Address for shipping/tax correct" },
    ],
  },
  {
    title: "4. Cart / products (UI)",
    icon: "ri-shopping-cart-line",
    items: [
      { id: "RC-C01", label: "Cart from context: items and prices correct" },
      { id: "RC-C02", label: "Quantity change: totals and tax recalc" },
      { id: "RC-C03", label: "Remove item: totals and tax recalc" },
    ],
  },
  {
    title: "5. Shipping options (UI + payload)",
    icon: "ri-truck-line",
    items: [
      { id: "RC-S01", label: "Select manual shipping: amount and tax" },
      { id: "RC-S02", label: "Select UPS shipping: rates from address/dimensions" },
      { id: "RC-S03", label: "Edit shipping amount if allowed" },
      { id: "RC-S04", label: "order_charges in payload: shipping_option and specialfee" },
    ],
  },
  {
    title: "6. Special fees (UI)",
    icon: "ri-price-tag-3-line",
    items: [
      { id: "RC-F01", label: "Required fee: in totals and payload" },
      { id: "RC-F02", label: "Optional fee: in totals and payload" },
    ],
  },
  {
    title: "7. Tax (UI)",
    icon: "ri-calculator-line",
    items: [
      { id: "RC-T01", label: "Product tax: per-line and total" },
      { id: "RC-T02", label: "Shipping tax in totals" },
      { id: "RC-T03", label: "Special fee tax in totals" },
    ],
  },
  {
    title: "8. Coupon / discount (UI)",
    icon: "ri-coupon-line",
    items: [
      { id: "RC-D01", label: "Apply coupon: valid; cart meets min" },
      { id: "RC-D02", label: "Eligibility: cart below min_cart_value" },
      { id: "RC-D03", label: "Expiration: expired coupon not applied" },
    ],
  },
  {
    title: "9. Payment (UI)",
    icon: "ri-bank-card-line",
    items: [
      { id: "RC-P01", label: "Payment method: Stripe or manual" },
      { id: "RC-P02", label: "Stripe payment: success; order can place" },
    ],
  },
  {
    title: "10. Submit and payload (API)",
    icon: "ri-send-plane-line",
    items: [
      { id: "RC-X01", label: "Successful place order: order_charges; success; redirect or clear cart" },
      { id: "RC-X02", label: "order_charges structure: shipping_option and specialfee" },
      { id: "RC-X03", label: "Submit error: message shown; form stays editable" },
    ],
  },
  {
    title: "11. Summary checklist (Realtor Checkout)",
    icon: "ri-checkbox-multiple-line",
    items: [
      { id: "RC-SUM1", label: "Validation: name; office/team member/agent when required; billing; shipping; shipping option; required fees" },
      { id: "RC-SUM2", label: "User type: agent vs team member; cascade on office change" },
      { id: "RC-SUM3", label: "Address: same-as-billing, different, add address" },
      { id: "RC-SUM4", label: "Cart: items from context; totals and tax" },
      { id: "RC-SUM5", label: "Shipping: manual/UPS; order_charges.shipping_option in payload" },
      { id: "RC-SUM6", label: "Special fees: required/optional in payload" },
      { id: "RC-SUM7", label: "Tax: product, shipping, special fee" },
      { id: "RC-SUM8", label: "Coupon: apply, eligibility, expiration" },
      { id: "RC-SUM9", label: "Payment: Stripe or manual" },
      { id: "RC-SUM10", label: "Submit: success redirect; error handling" },
    ],
  },
];

const ROUTES_DATA = [
  { path: "/signage/orders", type: "list" as const, component: "SignageOrders", description: "Orders list" },
  { path: "/signage/createOrder", type: "create" as const, component: "SignageCreateOrder", description: "Create new order" },
  { path: "/signage/orderDetails/:orderId", type: "view" as const, component: "SignageOrderDetails", description: "View order details" },
  { path: "/signage/editOrder/:orderId", type: "edit" as const, component: "SignageEditOrder", description: "Edit existing order" },
  { path: "/signageteammember/createOrder", type: "create" as const, component: "SignageCreateOrder", description: "Create (team member)" },
  { path: "/signageteammember/orderDetails/:orderId", type: "view" as const, component: "SignageOrderDetails", description: "View (team member)" },
  { path: "/signageteammember/editOrder/:orderId", type: "edit" as const, component: "SignageEditOrder", description: "Edit (team member)" },
];

const TAB_IDS_WITH_SECTIONS: string[] = ["create", "view", "edit", "agent-checkout", "realtor-checkout", "installation-create", "installation-view", "installation-edit", "realtor-installation-create", "realtor-installation-view", "agent-installation-create", "agent-installation-view", "agent-installation-edit"];

function getDefaultSections(): SectionsState {
  return {
    create: CREATE_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-create-${si}`, items: s.items.map((i) => ({ ...i })) })),
    view: VIEW_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-view-${si}`, items: s.items.map((i) => ({ ...i })) })),
    edit: EDIT_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-edit-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "agent-checkout": AGENT_CHECKOUT_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-agent-checkout-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "realtor-checkout": REALTOR_CHECKOUT_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-realtor-checkout-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "installation-create": INSTALLATION_CREATE_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-installation-create-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "installation-view": INSTALLATION_VIEW_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-installation-view-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "installation-edit": INSTALLATION_EDIT_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-installation-edit-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "realtor-installation-create": REALTOR_INSTALLATION_CREATE_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-realtor-installation-create-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "realtor-installation-view": REALTOR_INSTALLATION_VIEW_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-realtor-installation-view-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "agent-installation-create": AGENT_INSTALLATION_CREATE_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-agent-installation-create-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "agent-installation-view": AGENT_INSTALLATION_VIEW_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-agent-installation-view-${si}`, items: s.items.map((i) => ({ ...i })) })),
    "agent-installation-edit": AGENT_INSTALLATION_EDIT_SECTIONS.map((s, si) => ({ ...s, sectionId: `def-agent-installation-edit-${si}`, items: s.items.map((i) => ({ ...i })) })),
  };
}

function loadSections(): SectionsState {
  try {
    const raw = localStorage.getItem(SECTIONS_STORAGE_KEY);
    const defaultSections = getDefaultSections();
    if (raw) {
      const parsed = JSON.parse(raw) as SectionsState;
      if (parsed && typeof parsed === "object") {
        const merged = { ...defaultSections, ...parsed };
        // Ensure every section has stable sectionId for check keys and delete
        TAB_IDS_WITH_SECTIONS.forEach((tabKey) => {
          const list = merged[tabKey];
          if (!Array.isArray(list)) return;
          merged[tabKey] = list.map((sec, si) => ({
            ...sec,
            sectionId: sec.sectionId ?? `def-${tabKey}-${si}`,
          }));
        });
        return merged;
      }
    }
    return defaultSections;
  } catch (_) {}
  return getDefaultSections();
}

function saveSections(sections: SectionsState) {
  try {
    localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(sections));
  } catch (_) {}
}

function getAllCheckKeys(sections: SectionsState): string[] {
  const keys: string[] = [];
  TAB_IDS_WITH_SECTIONS.forEach((tabKey) => {
    const list = sections[tabKey];
    if (!Array.isArray(list)) return;
    list.forEach((sec, si) => {
      const prefix = sec.sectionId ?? `i-${si}`;
      sec.items.forEach((item) => keys.push(`${tabKey}-${prefix}-${item.id}`));
    });
  });
  return keys;
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function loadChecks(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return {};
}

function saveChecks(checks: Record<string, boolean>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checks));
  } catch (_) {}
}

// ─── Checklist card with section icon, progress, add/edit/delete ─────────────
function ChecklistCard({
  theme,
  title,
  icon,
  items,
  checkKeyPrefix,
  checks,
  onToggle,
  onAdd,
  onEdit,
  onDelete,
  onEditSection,
  onDeleteSection,
}: {
  theme: Theme;
  title: string;
  icon: string;
  items: { id: string; label: string }[];
  checkKeyPrefix: string;
  checks: Record<string, boolean>;
  onToggle: (key: string, value: boolean) => void;
  onAdd: () => void;
  onEdit: (itemIndex: number, item: { id: string; label: string }) => void;
  onDelete: (itemIndex: number, item: { id: string; label: string }) => void;
  onEditSection?: () => void;
  onDeleteSection?: () => void;
}) {
  const done = items.filter((item) => checks[`${checkKeyPrefix}-${item.id}`]).length;
  const total = items.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div
      className="mb-4 overflow-hidden"
      style={{
        borderRadius: theme.radius.lg,
        background: theme.bg.card,
        border: `1px solid ${theme.border}`,
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        maxWidth: "100%",
      }}
    >
      <div
        className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 px-md-4 py-3"
        style={{ borderBottom: `1px solid ${theme.border}` }}
      >
        <h6 className="mb-0 d-flex align-items-center gap-2 flex-grow-1" style={{ color: theme.text.primary, fontWeight: 600, fontSize: "clamp(0.85rem, 2.5vw, 0.95rem)", wordBreak: "break-word", minWidth: 0 }}>
          <i className={icon} style={{ fontSize: "1rem", color: theme.accent, flexShrink: 0 }} />
          <span style={{ minWidth: 0 }}>{title}</span>
        </h6>
        <div className="d-flex align-items-center gap-2 gap-md-3 flex-shrink-0">
          {(onEditSection || onDeleteSection) && (
            <div className="d-flex align-items-center gap-1">
              {onEditSection && (
                <button
                  type="button"
                  className="btn btn-sm border-0 p-1"
                  onClick={onEditSection}
                  title="Edit card (title & icon)"
                  style={{ color: theme.text.muted }}
                >
                  <i className="ri-pencil-line" />
                </button>
              )}
              {onDeleteSection && (
                <button
                  type="button"
                  className="btn btn-sm border-0 p-1"
                  onClick={onDeleteSection}
                  title="Delete card"
                  style={{ color: theme.text.muted }}
                >
                  <i className="ri-delete-bin-line" />
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            className="btn btn-sm border-0 d-flex align-items-center gap-1"
            onClick={onAdd}
            style={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: theme.accent,
              background: theme.accentMuted,
              borderRadius: theme.radius.sm,
              padding: "4px 10px",
            }}
          >
            <i className="ri-add-line" /> Add test case
          </button>
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: 56,
                height: 6,
                borderRadius: 3,
                background: "rgba(148, 163, 184, 0.2)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: pct === 100 ? theme.success : theme.accent,
                  borderRadius: 3,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <span style={{ fontFamily: theme.fontMono, fontSize: "0.75rem", color: theme.text.muted }}>
              {done}/{total}
            </span>
          </div>
        </div>
      </div>
      <div className="px-3 px-md-4 py-3">
        <ul className="list-unstyled mb-0">
          {items.map((item, i) => {
            const key = `${checkKeyPrefix}-${item.id}`;
            const checked = !!checks[key];
            return (
              <li
                key={key}
                className="d-flex align-items-start gap-2 gap-md-3 py-2 px-2 px-md-3 rounded-2"
                style={{
                  transition: "background 0.15s ease",
                  marginBottom: 4,
                  background: checked ? theme.accentMuted : "transparent",
                }}
              >
                <input
                  type="checkbox"
                  id={key}
                  className="form-check-input flex-shrink-0 mt-1"
                  checked={checked}
                  onChange={(e) => onToggle(key, e.target.checked)}
                  style={{
                    width: 18,
                    height: 18,
                    accentColor: theme.accent,
                    cursor: "pointer",
                  }}
                />
                <label
                  htmlFor={key}
                  className="form-check-label flex-grow-1 mb-0 small"
                  style={{
                    cursor: "pointer",
                    color: checked ? theme.text.primary : theme.text.secondary,
                    lineHeight: 1.45,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    minWidth: 0,
                  }}
                >
                  <span
                    className="me-2"
                    style={{
                      fontFamily: theme.fontMono,
                      fontSize: "0.7rem",
                      color: theme.text.muted,
                      background: "rgba(148, 163, 184, 0.15)",
                      padding: "2px 6px",
                      borderRadius: theme.radius.sm,
                    }}
                  >
                    {item.id}
                  </span>
                  {item.label}
                </label>
                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    className="btn btn-sm border-0 p-1"
                    onClick={() => onEdit(i, item)}
                    title="Edit"
                    style={{ color: theme.text.muted }}
                  >
                    <i className="ri-pencil-line" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm border-0 p-1"
                    onClick={() => onDelete(i, item)}
                    title="Delete"
                    style={{ color: theme.text.muted }}
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ─── Routes table row with copy ────────────────────────────────────────────
function RouteRow({
  theme,
  path,
  type,
  component,
  description,
}: {
  theme: Theme;
  path: string;
  type: "create" | "view" | "edit" | "list";
  component: string;
  description: string;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(path).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const typeColors: Record<string, { bg: string; text: string }> = {
    create: { bg: "rgba(34, 211, 238, 0.2)", text: theme.accent },
    view: { bg: "rgba(52, 211, 153, 0.2)", text: theme.success },
    edit: { bg: "rgba(251, 191, 36, 0.2)", text: "#f59e0b" },
    list: { bg: "rgba(148, 163, 184, 0.2)", text: theme.text.secondary },
  };
  const { bg: typeBg, text: typeText } = typeColors[type] || typeColors.list;
  return (
    <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
      <td className="py-3 align-middle">
        <code className="small" style={{ fontFamily: theme.fontMono, color: theme.text.primary }}>{path}</code>
      </td>
      <td className="py-3 align-middle">
        <span style={{ fontSize: "0.75rem", fontWeight: 600, background: typeBg, color: typeText, padding: "4px 8px", borderRadius: theme.radius.sm }}>
          {type === "list" ? "List" : type.charAt(0).toUpperCase() + type.slice(1)}
        </span>
      </td>
      <td className="py-3 align-middle small" style={{ color: theme.text.muted }}>{component}</td>
      <td className="py-3 align-middle small" style={{ color: theme.text.secondary }}>{description}</td>
      <td className="py-3 align-middle text-end">
        <button
          type="button"
          className="btn btn-sm border-0"
          onClick={copy}
          title="Copy path"
          style={{
            background: "rgba(148, 163, 184, 0.15)",
            color: theme.text.secondary,
            borderRadius: theme.radius.sm,
            padding: "6px 10px",
          }}
        >
          {copied ? <i className="ri-check-line" style={{ color: theme.success }} /> : <i className="ri-file-copy-line" />}
        </button>
      </td>
    </tr>
  );
}

// ─── Modal: Add / Edit test case ──────────────────────────────────────────
function TestCaseModal({
  show,
  mode,
  initialId,
  initialLabel,
  onSave,
  onClose,
  theme,
}: {
  show: boolean;
  mode: "add" | "edit";
  initialId: string;
  initialLabel: string;
  onSave: (id: string, label: string) => void;
  onClose: () => void;
  theme: Theme;
}) {
  const [id, setId] = useState(initialId);
  const [label, setLabel] = useState(initialLabel);

  React.useEffect(() => {
    if (show) {
      setId(initialId);
      setLabel(initialLabel);
    }
  }, [show, initialId, initialLabel]);

  if (!show) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const tid = id.trim();
    const tlabel = label.trim();
    if (tid && tlabel) {
      onSave(tid, tlabel);
      onClose();
    }
  };

  return (
    <div
      className="fixed-top fixed-bottom d-flex align-items-center justify-content-center p-3"
      style={{ background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      onClick={onClose}
    >
      <div
        className="rounded-3 shadow-lg p-4"
        style={{
          width: "100%",
          maxWidth: 440,
          background: theme.bg.card,
          border: `1px solid ${theme.border}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h6 className="mb-3 fw-semibold" style={{ color: theme.text.primary }}>
          {mode === "add" ? "Add test case" : "Edit test case"}
        </h6>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label small mb-1" style={{ color: theme.text.secondary }}>Test ID</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="e.g. C-V05"
              style={{
                fontFamily: theme.fontMono,
                background: theme.bg.content,
                borderColor: theme.border,
                color: theme.text.primary,
              }}
            />
          </div>
          <div className="mb-4">
            <label className="form-label small mb-1" style={{ color: theme.text.secondary }}>Description</label>
            <textarea
              className="form-control form-control-sm"
              rows={3}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Test case description..."
              style={{
                background: theme.bg.content,
                borderColor: theme.border,
                color: theme.text.primary,
              }}
            />
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-sm" onClick={onClose} style={{ color: theme.text.secondary }}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-sm border-0"
              style={{
                background: theme.accent,
                color: "#fff",
                fontWeight: 600,
                padding: "6px 16px",
                borderRadius: theme.radius.sm,
              }}
            >
              {mode === "add" ? "Add" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal: Add / Edit card (section: title + icon) ─────────────────────────
const SECTION_ICON_OPTIONS = [
  "ri-add-circle-line",
  "ri-error-warning-line",
  "ri-user-settings-line",
  "ri-map-pin-line",
  "ri-user-star-line",
  "ri-calendar-check-line",
  "ri-layout-grid-line",
  "ri-shopping-cart-line",
  "ri-price-tag-3-line",
  "ri-calculator-line",
  "ri-coupon-3-line",
  "ri-file-text-line",
  "ri-send-plane-line",
  "ri-route-line",
  "ri-eye-line",
  "ri-edit-line",
  "ri-flow-chart",
  "ri-links-line",
  "ri-download-cloud-line",
  "ri-lock-line",
  "ri-folder-line",
  "ri-checkbox-multiple-line",
];

function SectionModal({
  show,
  mode,
  initialTitle,
  initialIcon,
  onSave,
  onClose,
  theme,
}: {
  show: boolean;
  mode: "add" | "edit";
  initialTitle: string;
  initialIcon: string;
  onSave: (title: string, icon: string) => void;
  onClose: () => void;
  theme: Theme;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [icon, setIcon] = useState(initialIcon);

  React.useEffect(() => {
    if (show) {
      setTitle(initialTitle);
      setIcon(initialIcon);
    }
  }, [show, initialTitle, initialIcon]);

  if (!show) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    const i = icon.trim() || "ri-folder-line";
    if (t) {
      onSave(t, i);
      onClose();
    }
  };

  return (
    <div
      className="fixed-top fixed-bottom d-flex align-items-center justify-content-center p-3"
      style={{ background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      onClick={onClose}
    >
      <div
        className="rounded-3 shadow-lg p-4"
        style={{
          width: "100%",
          maxWidth: 440,
          background: theme.bg.card,
          border: `1px solid ${theme.border}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h6 className="mb-3 fw-semibold" style={{ color: theme.text.primary }}>
          {mode === "add" ? "Add new card" : "Edit card"}
        </h6>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label small mb-1" style={{ color: theme.text.secondary }}>Card title</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. 14. My custom section"
              style={{
                background: theme.bg.content,
                borderColor: theme.border,
                color: theme.text.primary,
              }}
            />
          </div>
          <div className="mb-4">
            <label className="form-label small mb-1" style={{ color: theme.text.secondary }}>Icon (Remix icon class)</label>
            <select
              className="form-select form-select-sm"
              value={icon || "ri-folder-line"}
              onChange={(e) => setIcon(e.target.value)}
              style={{
                background: theme.bg.content,
                borderColor: theme.border,
                color: theme.text.primary,
              }}
            >
              {SECTION_ICON_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <small className="text-muted mt-1 d-block">You can add test cases to this card after saving.</small>
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-sm" onClick={onClose} style={{ color: theme.text.secondary }}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-sm border-0"
              style={{
                background: theme.accent,
                color: "#fff",
                fontWeight: 600,
                padding: "6px 16px",
                borderRadius: theme.radius.sm,
              }}
            >
              {mode === "add" ? "Add card" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
const SignageOrdersDocs: React.FC = () => {
  const location = useLocation();
  const baseRoute = location.pathname.includes("/signageteammember") ? "/signageteammember" : "/signage";
  const ordersPath = `${baseRoute}/orders`;

  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    try {
      const t = localStorage.getItem(THEME_STORAGE_KEY);
      if (t === "dark" || t === "light") return t;
    } catch (_) {}
    return "dark";
  });
  const theme = themeMode === "dark" ? themeDark : themeLight;

  const [activeTab, setActiveTab] = useState<TabId>("create");
  const [sections, setSections] = useState<SectionsState>(loadSections);
  const [checks, setChecks] = useState<Record<string, boolean>>(loadChecks);

  const [modal, setModal] = useState<{
    mode: "add" | "edit";
    tabKey: string;
    sectionIndex: number;
    itemIndex: number;
    initialId: string;
    initialLabel: string;
  } | null>(null);

  const [sectionModal, setSectionModal] = useState<{
    mode: "add" | "edit";
    tabKey: string;
    sectionIndex: number;
    initialTitle: string;
    initialIcon: string;
  } | null>(null);

  useEffect(() => {
    setChecks(loadChecks());
  }, []);

  // One-time migration: old check keys used tabKey-sectionIndex-itemId; now we use tabKey-sectionId-itemId
  const didMigrateChecks = useRef(false);
  useEffect(() => {
    if (didMigrateChecks.current) return;
    didMigrateChecks.current = true;
    const next = { ...checks };
    let changed = false;
    TAB_IDS_WITH_SECTIONS.forEach((tabKey) => {
      const list = sections[tabKey];
      if (!Array.isArray(list)) return;
      list.forEach((sec, si) => {
        const sectionId = sec.sectionId ?? `def-${tabKey}-${si}`;
        sec.items.forEach((item) => {
          const oldKey = `${tabKey}-${si}-${item.id}`;
          const newKey = `${tabKey}-${sectionId}-${item.id}`;
          if (oldKey !== newKey && next[oldKey] !== undefined) {
            next[newKey] = next[oldKey];
            delete next[oldKey];
            changed = true;
          }
        });
      });
    });
    if (changed) {
      saveChecks(next);
      setChecks(next);
    }
  }, [sections]);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch (_) {}
  }, [themeMode]);

  const onToggle = useCallback((key: string, value: boolean) => {
    setChecks((prev) => {
      const next = { ...prev, [key]: value };
      saveChecks(next);
      return next;
    });
  }, []);

  const openAddModal = (tabKey: string, sectionIndex: number) => {
    setModal({
      mode: "add",
      tabKey,
      sectionIndex,
      itemIndex: -1,
      initialId: "",
      initialLabel: "",
    });
  };

  const openEditModal = (tabKey: string, sectionIndex: number, itemIndex: number, item: { id: string; label: string }) => {
    setModal({
      mode: "edit",
      tabKey,
      sectionIndex,
      itemIndex,
      initialId: item.id,
      initialLabel: item.label,
    });
  };

  const closeModal = () => setModal(null);

  const saveTestCase = useCallback(
    (id: string, label: string) => {
      if (!modal) return;
      const { tabKey, sectionIndex, itemIndex, mode } = modal;
      const list = sections[tabKey];
      if (!list || !list[sectionIndex]) return;
      const sec = list[sectionIndex];
      const prefix = sec.sectionId ?? `i-${sectionIndex}`;
      const nextSections = { ...sections };
      const nextList = nextSections[tabKey].map((s, i) =>
        i !== sectionIndex ? s : { ...s, items: s.items.map((it) => ({ ...it })) }
      );
      const s = nextList[sectionIndex];
      if (mode === "add") {
        nextList[sectionIndex] = { ...s, items: [...s.items, { id, label }] };
      } else {
        const oldId = s.items[itemIndex]?.id;
        const newItems = s.items.map((it, i) => (i === itemIndex ? { id, label } : { ...it }));
        nextList[sectionIndex] = { ...s, items: newItems };
        if (oldId && oldId !== id) {
          const oldKey = `${tabKey}-${prefix}-${oldId}`;
          setChecks((prev) => {
            const next = { ...prev };
            delete next[oldKey];
            saveChecks(next);
            return next;
          });
        }
      }
      nextSections[tabKey] = nextList;
      setSections(nextSections);
      saveSections(nextSections);
      closeModal();
    },
    [modal, sections]
  );

  const deleteTestCase = useCallback(
    (tabKey: string, sectionIndex: number, itemIndex: number, item: { id: string; label: string }) => {
      if (!window.confirm(`Delete test case "${item.id}"?`)) return;
      const list = sections[tabKey];
      if (!list || !list[sectionIndex]) return;
      const nextSections = { ...sections };
      const nextList = nextSections[tabKey].map((sec, i) =>
        i !== sectionIndex ? sec : { ...sec, items: sec.items.filter((_, ii) => ii !== itemIndex) }
      );
      nextSections[tabKey] = nextList;
      setSections(nextSections);
      saveSections(nextSections);
      const sec = list[sectionIndex];
      const prefix = sec.sectionId ?? `i-${sectionIndex}`;
      const key = `${tabKey}-${prefix}-${item.id}`;
      setChecks((prev) => {
        const next = { ...prev };
        delete next[key];
        saveChecks(next);
        return next;
      });
    },
    [sections]
  );

  const openAddSectionModal = (tabKey: string) => {
    setSectionModal({
      mode: "add",
      tabKey,
      sectionIndex: (sections[tabKey] ?? []).length,
      initialTitle: "",
      initialIcon: "ri-folder-line",
    });
  };

  const openEditSectionModal = (tabKey: string, sectionIndex: number) => {
    const list = sections[tabKey];
    const sec = list?.[sectionIndex];
    if (!sec) return;
    setSectionModal({
      mode: "edit",
      tabKey,
      sectionIndex,
      initialTitle: sec.title,
      initialIcon: sec.icon || "ri-folder-line",
    });
  };

  const closeSectionModal = () => setSectionModal(null);

  const saveSection = useCallback(
    (title: string, icon: string) => {
      if (!sectionModal) return;
      const { tabKey, sectionIndex, mode } = sectionModal;
      const nextSections = { ...sections };
      const list = nextSections[tabKey] ?? [];
      if (mode === "add") {
        const newSection: Section = {
          sectionId: `sec-${Date.now()}`,
          title: title.trim() || "New section",
          icon: icon.trim() || "ri-folder-line",
          items: [],
        };
        nextSections[tabKey] = [...list, newSection];
      } else {
        if (list[sectionIndex]) {
          const nextList = list.map((sec, i) =>
            i !== sectionIndex ? sec : { ...sec, title: title.trim() || sec.title, icon: icon.trim() || sec.icon }
          );
          nextSections[tabKey] = nextList;
        }
      }
      setSections(nextSections);
      saveSections(nextSections);
      closeSectionModal();
    },
    [sectionModal, sections]
  );

  const deleteSection = useCallback(
    (tabKey: string, sectionIndex: number) => {
      const list = sections[tabKey];
      const sec = list?.[sectionIndex];
      if (!sec || !window.confirm(`Delete card "${sec.title}" and all its test cases?`)) return;
      const prefix = sec.sectionId ?? `i-${sectionIndex}`;
      const nextSections = { ...sections };
      nextSections[tabKey] = list.filter((_, i) => i !== sectionIndex);
      setSections(nextSections);
      saveSections(nextSections);
      setChecks((prev) => {
        const next = { ...prev };
        const pre = `${tabKey}-${prefix}-`;
        Object.keys(next).forEach((k) => {
          if (k.startsWith(pre)) delete next[k];
        });
        saveChecks(next);
        return next;
      });
    },
    [sections]
  );

  const allKeys = getAllCheckKeys(sections);
  const total = allKeys.length;
  const done = allKeys.filter((k) => checks[k]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const TABS: { id: TabId; label: string; icon: string }[] = [
    { id: "create", label: "Create", icon: "ri-add-circle-line" },
    { id: "view", label: "View", icon: "ri-eye-line" },
    { id: "edit", label: "Edit", icon: "ri-edit-line" },
    { id: "agent-checkout", label: "Agent Checkout", icon: "ri-shopping-bag-line" },
    { id: "realtor-checkout", label: "Realtor Checkout", icon: "ri-store-2-line" },
    { id: "routes", label: "Routes", icon: "ri-route-line" },
    { id: "installation-create", label: "Installation Create", icon: "ri-add-circle-line" },
    { id: "installation-view", label: "Installation View", icon: "ri-eye-line" },
    { id: "installation-edit", label: "Installation Edit", icon: "ri-edit-line" },
    { id: "realtor-installation-create", label: "Realtor Add Installation", icon: "ri-add-circle-line" },
    { id: "realtor-installation-view", label: "Realtor View Installation", icon: "ri-eye-line" },
    { id: "agent-installation-create", label: "Agent Add Installation", icon: "ri-add-circle-line" },
    { id: "agent-installation-view", label: "Agent View Installation", icon: "ri-eye-line" },
    { id: "agent-installation-edit", label: "Agent Edit Installation", icon: "ri-edit-line" },
  ];

  const LICENSE_TYPES = [
    { key: "signage", label: "Signage" },
    { key: "signage-team-member", label: "Signage team member" },
    { key: "brokerage", label: "Brokerage" },
    { key: "brokerage-team-member", label: "Brokerage team member" },
    { key: "brokerage-agent", label: "Brokerage agent" },
    { key: "direct-agent", label: "Direct agent" },
  ] as const;

  /** Orders submodule items per license (exact per-user mapping) */
  const ORDER_ITEMS_BY_LICENSE: Record<string, TabId[]> = {
    signage: ["create", "view", "edit"],
    "signage-team-member": ["create", "view", "edit"],
    brokerage: ["view", "realtor-checkout"],
    "brokerage-team-member": ["view", "realtor-checkout"],
    "brokerage-agent": ["view", "agent-checkout"],
    "direct-agent": ["view", "agent-checkout"],
  };

  const [expandedLicense, setExpandedLicense] = useState<string | null>("signage");

  const resetProgress = () => {
    if (window.confirm("Reset all test progress?")) {
      setChecks({});
      saveChecks({});
    }
  };

  const [leftNavOpen, setLeftNavOpen] = useState(() => {
    try {
      if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) return false;
      const v = localStorage.getItem("orders_docs_left_nav_open");
      if (v === "0" || v === "1") return v === "1";
    } catch (_) {}
    return true;
  });
  useEffect(() => {
    try {
      localStorage.setItem("orders_docs_left_nav_open", leftNavOpen ? "1" : "0");
    } catch (_) {}
  }, [leftNavOpen]);

  // Responsive: treat as mobile below 768px; sidebar becomes overlay
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const mq = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    const media = window.matchMedia("(max-width: 767px)");
    setIsMobile(media.matches);
    media.addEventListener("change", mq);
    return () => media.removeEventListener("change", mq);
  }, []);

  const handleNavTabSelect = useCallback((tabId: TabId) => {
    setActiveTab(tabId);
    if (isMobile) setLeftNavOpen(false);
  }, [isMobile]);

  return (
    <div
      className="content d-flex"
      style={{
        paddingBottom: 32,
        minHeight: "100vh",
        background: theme.bg.content,
        overflowX: "hidden",
        maxWidth: "100%",
      }}
    >
      {/* Backdrop when sidebar is open on mobile */}
      {isMobile && leftNavOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onClick={() => setLeftNavOpen(false)}
          onKeyDown={(e) => e.key === "Enter" && setLeftNavOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1040,
            transition: "opacity 0.2s ease",
          }}
        />
      )}
      {/* ─── Left navbar: overlay on mobile, inline on desktop ───────────────── */}
      <aside
        role="navigation"
        aria-label="Main navigation"
        style={
          isMobile
            ? {
                position: "fixed",
                left: 0,
                top: 0,
                height: "100vh",
                width: 280,
                maxWidth: "85vw",
                flexShrink: 0,
                transition: "transform 0.25s ease",
                transform: leftNavOpen ? "translateX(0)" : "translateX(-100%)",
                zIndex: 1050,
                background: theme.bg.sidebar,
                borderRight: `1px solid ${theme.border}`,
                display: "flex",
                flexDirection: "column",
                boxShadow: leftNavOpen ? "4px 0 24px rgba(0,0,0,0.2)" : "none",
              }
            : {
                width: leftNavOpen ? 280 : 56,
                minWidth: leftNavOpen ? 280 : 56,
                flexShrink: 0,
                transition: "width 0.2s ease",
                background: theme.bg.sidebar,
                borderRight: `1px solid ${theme.border}`,
                display: "flex",
                flexDirection: "column",
              }
        }
      >
        <div
          className="d-flex align-items-center px-3"
          style={{
            minHeight: 56,
            borderBottom: leftNavOpen ? `1px solid ${theme.border}` : "none",
            justifyContent: leftNavOpen ? "space-between" : "center",
          }}
        >
          {leftNavOpen ? (
            <span className="fw-semibold small" style={{ color: theme.text.primary }}>
              Menu
            </span>
          ) : null}
          <button
            type="button"
            className="btn btn-sm border-0 d-flex align-items-center justify-content-center rounded-2"
            onClick={() => setLeftNavOpen(!leftNavOpen)}
            title={leftNavOpen ? "Close sidebar" : "Open sidebar"}
            style={{
              width: 36,
              height: 36,
              color: theme.text.secondary,
              background: leftNavOpen ? "transparent" : theme.accentMuted,
            }}
          >
            {leftNavOpen ? (
              <i className="ri-sidebar-fold-line" style={{ fontSize: "1.2rem" }} />
            ) : (
              <i className="ri-sidebar-unfold-line" style={{ fontSize: "1.2rem" }} />
            )}
          </button>
        </div>
        {leftNavOpen && (
          <div className="py-2 flex-grow-1 overflow-auto">
            {LICENSE_TYPES.map((license) => {
              const isExpanded = expandedLicense === license.key;
              return (
                <div key={license.key} style={{ marginBottom: 4 }}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedLicense(isExpanded ? null : license.key)}
                    onKeyDown={(e) => e.key === "Enter" && setExpandedLicense(isExpanded ? null : license.key)}
                    className="d-flex align-items-center gap-2 px-3 py-2"
                    style={{
                      marginLeft: 8,
                      marginRight: 8,
                      borderRadius: theme.radius.sm,
                      background: isExpanded ? "rgba(148, 163, 184, 0.12)" : "transparent",
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                      color: theme.text.primary,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    <i
                      className={isExpanded ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"}
                      style={{ fontSize: "1rem", flexShrink: 0, color: theme.text.muted }}
                    />
                    <span>{license.label}</span>
                  </div>
                  {isExpanded && (
                    <div style={{ paddingLeft: 12, paddingRight: 8, paddingBottom: 8 }}>
                      <div
                        className="small py-1 px-2 mb-1"
                        style={{
                          color: theme.text.muted,
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          letterSpacing: "0.03em",
                        }}
                      >
                        Orders
                      </div>
                      {(ORDER_ITEMS_BY_LICENSE[license.key] ?? []).map((tabId) => {
                        const tab = TABS.find((t) => t.id === tabId);
                        if (!tab) return null;
                        const isActive = activeTab === tab.id;
                        return (
                          <div
                            key={tab.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleNavTabSelect(tab.id)}
                            onKeyDown={(e) => e.key === "Enter" && handleNavTabSelect(tab.id)}
                            className="d-flex align-items-center gap-2 py-2 px-2 rounded-1"
                            style={{
                              cursor: "pointer",
                              transition: "background 0.15s ease",
                              background: isActive ? theme.accentMuted : "transparent",
                              color: isActive ? theme.accent : theme.text.secondary,
                              fontSize: "0.8125rem",
                            }}
                          >
                            <i className={tab.icon} style={{ fontSize: "1rem", flexShrink: 0, width: 20, textAlign: "center" }} />
                            <span style={{ fontWeight: isActive ? 600 : 500 }}>{tab.label}</span>
                          </div>
                        );
                      })}
                      {(license.key === "signage" || license.key === "signage-team-member") && (
                        <>
                          <div
                            className="small py-1 px-2 mb-1 mt-2"
                            style={{
                              color: theme.text.muted,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              letterSpacing: "0.03em",
                            }}
                          >
                            Installation
                          </div>
                          {(["installation-create", "installation-view", "installation-edit"] as const).map((tabId) => {
                            const tab = TABS.find((t) => t.id === tabId);
                            if (!tab) return null;
                            const isActive = activeTab === tab.id;
                            return (
                              <div
                                key={tab.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleNavTabSelect(tab.id)}
                                onKeyDown={(e) => e.key === "Enter" && handleNavTabSelect(tab.id)}
                                className="d-flex align-items-center gap-2 py-2 px-2 rounded-1"
                                style={{
                                  cursor: "pointer",
                                  transition: "background 0.15s ease",
                                  background: isActive ? theme.accentMuted : "transparent",
                                  color: isActive ? theme.accent : theme.text.secondary,
                                  fontSize: "0.8125rem",
                                }}
                              >
                                <i className={tab.icon} style={{ fontSize: "1rem", flexShrink: 0, width: 20, textAlign: "center" }} />
                                <span style={{ fontWeight: isActive ? 600 : 500 }}>{tab.label}</span>
                              </div>
                            );
                          })}
                        </>
                      )}
                      {(license.key === "brokerage" || license.key === "brokerage-team-member") && (
                        <>
                          <div
                            className="small py-1 px-2 mb-1 mt-2"
                            style={{
                              color: theme.text.muted,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              letterSpacing: "0.03em",
                            }}
                          >
                            Installation
                          </div>
                          {(["realtor-installation-create", "realtor-installation-view"] as const).map((tabId) => {
                            const tab = TABS.find((t) => t.id === tabId);
                            if (!tab) return null;
                            const isActive = activeTab === tab.id;
                            return (
                              <div
                                key={tab.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleNavTabSelect(tab.id)}
                                onKeyDown={(e) => e.key === "Enter" && handleNavTabSelect(tab.id)}
                                className="d-flex align-items-center gap-2 py-2 px-2 rounded-1"
                                style={{
                                  cursor: "pointer",
                                  transition: "background 0.15s ease",
                                  background: isActive ? theme.accentMuted : "transparent",
                                  color: isActive ? theme.accent : theme.text.secondary,
                                  fontSize: "0.8125rem",
                                }}
                              >
                                <i className={tab.icon} style={{ fontSize: "1rem", flexShrink: 0, width: 20, textAlign: "center" }} />
                                <span style={{ fontWeight: isActive ? 600 : 500 }}>{tab.label}</span>
                              </div>
                            );
                          })}
                        </>
                      )}
                      {(license.key === "brokerage-agent" || license.key === "direct-agent") && (
                        <>
                          <div
                            className="small py-1 px-2 mb-1 mt-2"
                            style={{
                              color: theme.text.muted,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              letterSpacing: "0.03em",
                            }}
                          >
                            Installation
                          </div>
                          {(["agent-installation-create", "agent-installation-view", "agent-installation-edit"] as const).map((tabId) => {
                            const tab = TABS.find((t) => t.id === tabId);
                            if (!tab) return null;
                            const isActive = activeTab === tab.id;
                            return (
                              <div
                                key={tab.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleNavTabSelect(tab.id)}
                                onKeyDown={(e) => e.key === "Enter" && handleNavTabSelect(tab.id)}
                                className="d-flex align-items-center gap-2 py-2 px-2 rounded-1"
                                style={{
                                  cursor: "pointer",
                                  transition: "background 0.15s ease",
                                  background: isActive ? theme.accentMuted : "transparent",
                                  color: isActive ? theme.accent : theme.text.secondary,
                                  fontSize: "0.8125rem",
                                }}
                              >
                                <i className={tab.icon} style={{ fontSize: "1rem", flexShrink: 0, width: 20, textAlign: "center" }} />
                                <span style={{ fontWeight: isActive ? 600 : 500 }}>{tab.label}</span>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </aside>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          ...(isMobile ? { paddingLeft: 12, paddingRight: 12 } : {}),
        }}
      >
      {/* ─── Top header ───────────────────────────────────────────────────── */}
      <div
        className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4 p-3 p-md-4 rounded-3"
        style={{ background: theme.bg.card, border: `1px solid ${theme.border}` }}
      >
        <div className="d-flex align-items-center gap-2 flex-grow-1 flex-wrap">
          {isMobile && (
            <button
              type="button"
              className="btn btn-sm border-0 d-flex align-items-center justify-content-center rounded-2"
              onClick={() => setLeftNavOpen(true)}
              title="Open menu"
              style={{
                width: 40,
                height: 40,
                color: theme.text.secondary,
                background: theme.accentMuted,
              }}
            >
              <i className="ri-menu-line" style={{ fontSize: "1.25rem" }} />
            </button>
          )}
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span style={{ fontFamily: theme.fontMono, fontSize: "0.7rem", color: theme.accent, fontWeight: 600, letterSpacing: "0.05em" }}>
                TESTING CENTER
              </span>
            </div>
            <h4 className="mb-1 fw-bold" style={{ color: theme.text.primary, fontSize: "clamp(1.1rem, 4vw, 1.5rem)" }}>
              Orders Test Cases
            </h4>
            <p className="mb-0 small" style={{ color: theme.text.secondary }}>
              Run and track Create, View, Edit, and Checkout flows. Progress saved in this browser.
            </p>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn btn-sm border-0 d-flex align-items-center gap-2"
            onClick={() => setThemeMode((m) => (m === "dark" ? "light" : "dark"))}
            title={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              background: theme.border,
              color: theme.text.secondary,
              borderRadius: theme.radius.md,
              padding: "8px 12px",
            }}
          >
            {themeMode === "dark" ? <i className="ri-sun-line" /> : <i className="ri-moon-line" />}
            {themeMode === "dark" ? "Light" : "Dark"}
          </button>
          <button
            type="button"
            className="btn btn-sm border-0"
            onClick={resetProgress}
            style={{
              background: "rgba(148, 163, 184, 0.2)",
              color: theme.text.secondary,
              borderRadius: theme.radius.md,
              padding: "8px 14px",
              fontWeight: 500,
            }}
          >
            Reset progress
          </button>
          <Link
            to={ordersPath}
            className="btn btn-sm border-0 d-flex align-items-center gap-2"
            style={{
              background: theme.accent,
              color: "#fff",
              borderRadius: theme.radius.md,
              padding: "8px 16px",
              fontWeight: 600,
            }}
          >
            <i className="ri-arrow-left-line" /> Back to Orders
          </Link>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="mb-4 p-3 p-md-4 rounded-3"
        style={{ background: theme.bg.card, border: `1px solid ${theme.border}` }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="small fw-semibold" style={{ color: theme.text.primary }}>Progress</span>
          <span style={{ fontFamily: theme.fontMono, fontSize: "0.8rem", color: theme.text.muted }}>
            {done} / {total} <span style={{ color: theme.accent }}>({pct}%)</span>
          </span>
        </div>
        <div
          className="rounded-3 overflow-hidden"
          style={{ height: 8, background: "rgba(148, 163, 184, 0.2)" }}
        >
          <div
            role="progressbar"
            className="rounded-3"
            style={{
              width: `${pct}%`,
              height: "100%",
              background: pct === 100 ? theme.success : `linear-gradient(90deg, ${theme.accent}, #06b6d4)`,
              transition: "width 0.4s ease",
            }}
          />
        </div>
      </div>

      {/* Tab pills — wrap on small screens */}
      <div className="d-flex flex-wrap gap-2 mb-4" style={{ gap: "0.5rem" }}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              className="btn border-0 d-flex align-items-center gap-1 gap-md-2"
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: isActive ? theme.accent : "rgba(148, 163, 184, 0.15)",
                color: isActive ? theme.bg.sidebar : theme.text.secondary,
                borderRadius: theme.radius.md,
                padding: "6px 10px",
                fontSize: "0.8125rem",
                fontWeight: isActive ? 600 : 500,
                transition: "background 0.2s ease, color 0.2s ease",
                whiteSpace: "nowrap",
              }}
            >
              <i className={tab.icon} style={{ fontSize: "1rem" }} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Main content (single column; nav is in left sidebar) */}
      <div className="row g-4 mx-0">
        <div className="col-12 px-2 px-md-3" style={{ maxWidth: "100%", overflowX: "hidden" }}>
          {/* Create tab */}
          {activeTab === "create" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-file-list-3-line" style={{ color: theme.accent }} />
                Create Order Test Cases
              </h5>
              {(sections.create || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`create-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("create", si)}
                  onEdit={(itemIndex, item) => openEditModal("create", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("create", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("create", si)}
                  onDeleteSection={() => deleteSection("create", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("create")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
              <div
                className="overflow-hidden rounded-3 mb-4"
                style={{ background: theme.bg.card, border: `1px solid ${theme.border}` }}
              >
                <div
                  className="py-3 px-4 d-flex align-items-center gap-2"
                  style={{ borderBottom: `1px solid ${theme.border}` }}
                >
                  <i className="ri-code-s-slash-line" style={{ color: theme.accent }} />
                  <h6 className="mb-0 fw-semibold" style={{ color: theme.text.primary }}>Request payload (reference)</h6>
                </div>
                <div className="p-4">
                  <pre
                    className="mb-0 small overflow-auto rounded-2 p-4"
                    style={{
                      maxHeight: 220,
                      fontFamily: theme.fontMono,
                      fontSize: "0.8rem",
                      background: "#0f172a",
                      color: "#94a3b8",
                      border: `1px solid ${theme.border}`,
                    }}
                  >
{`POST /orders
Content-Type: application/json

{
  "buyer_type": "agent" | "brokerageFirm",
  "items": [...],
  "order_charges": [{
    "shipping_option": [
      { "shipping_id", "shipping_amount", "is_customer_pickup", ... }
    ],
    "specialfee": [...]
  }],
  ...
}`}
                  </pre>
                </div>
              </div>
            </>
          )}

          {/* View tab */}
          {activeTab === "view" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-eye-line" style={{ color: theme.accent }} />
                View Order Test Cases
              </h5>
              {(sections.view || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`view-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("view", si)}
                  onEdit={(itemIndex, item) => openEditModal("view", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("view", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("view", si)}
                  onDeleteSection={() => deleteSection("view", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("view")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Edit tab */}
          {activeTab === "edit" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-edit-line" style={{ color: theme.accent }} />
                Edit Order Test Cases
              </h5>
              {(sections.edit || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`edit-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("edit", si)}
                  onEdit={(itemIndex, item) => openEditModal("edit", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("edit", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("edit", si)}
                  onDeleteSection={() => deleteSection("edit", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("edit")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Installation Create (Signage / Signage team member) */}
          {activeTab === "installation-create" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-add-circle-line" style={{ color: theme.accent }} />
                Installation Create Test Cases (Signage / Signage team member)
              </h5>
              {(sections["installation-create"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`installation-create-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("installation-create", si)}
                  onEdit={(itemIndex, item) => openEditModal("installation-create", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("installation-create", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("installation-create", si)}
                  onDeleteSection={() => deleteSection("installation-create", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("installation-create")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Installation View (Signage / Signage team member) */}
          {activeTab === "installation-view" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-eye-line" style={{ color: theme.accent }} />
                Installation View Test Cases (Signage / Signage team member)
              </h5>
              {(sections["installation-view"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`installation-view-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("installation-view", si)}
                  onEdit={(itemIndex, item) => openEditModal("installation-view", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("installation-view", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("installation-view", si)}
                  onDeleteSection={() => deleteSection("installation-view", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("installation-view")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Installation Edit (Signage / Signage team member) */}
          {activeTab === "installation-edit" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-edit-line" style={{ color: theme.accent }} />
                Installation Edit Test Cases (Signage / Signage team member)
              </h5>
              {(sections["installation-edit"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`installation-edit-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("installation-edit", si)}
                  onEdit={(itemIndex, item) => openEditModal("installation-edit", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("installation-edit", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("installation-edit", si)}
                  onDeleteSection={() => deleteSection("installation-edit", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("installation-edit")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Realtor Add Installation (Brokerage / Brokerage team member — RealtorAddInstallation) */}
          {activeTab === "realtor-installation-create" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-add-circle-line" style={{ color: theme.accent }} />
                Realtor Add Installation Test Cases (Brokerage / Brokerage team member)
              </h5>
              {(sections["realtor-installation-create"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`realtor-installation-create-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("realtor-installation-create", si)}
                  onEdit={(itemIndex, item) => openEditModal("realtor-installation-create", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("realtor-installation-create", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("realtor-installation-create", si)}
                  onDeleteSection={() => deleteSection("realtor-installation-create", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("realtor-installation-create")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Realtor View Installation (Brokerage / Brokerage team member — RealtorViewInstallation) */}
          {activeTab === "realtor-installation-view" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-eye-line" style={{ color: theme.accent }} />
                Realtor View Installation Test Cases (Brokerage / Brokerage team member)
              </h5>
              {(sections["realtor-installation-view"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`realtor-installation-view-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("realtor-installation-view", si)}
                  onEdit={(itemIndex, item) => openEditModal("realtor-installation-view", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("realtor-installation-view", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("realtor-installation-view", si)}
                  onDeleteSection={() => deleteSection("realtor-installation-view", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("realtor-installation-view")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Agent Add Installation (Brokerage agent / Direct agent — AgentAddInstallation) */}
          {activeTab === "agent-installation-create" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-add-circle-line" style={{ color: theme.accent }} />
                Agent Add Installation Test Cases (Brokerage agent / Direct agent)
              </h5>
              {(sections["agent-installation-create"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`agent-installation-create-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("agent-installation-create", si)}
                  onEdit={(itemIndex, item) => openEditModal("agent-installation-create", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("agent-installation-create", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("agent-installation-create", si)}
                  onDeleteSection={() => deleteSection("agent-installation-create", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("agent-installation-create")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Agent View Installation (Brokerage agent / Direct agent — AgentViewInstallation) */}
          {activeTab === "agent-installation-view" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-eye-line" style={{ color: theme.accent }} />
                Agent View Installation Test Cases (Brokerage agent / Direct agent)
              </h5>
              {(sections["agent-installation-view"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`agent-installation-view-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("agent-installation-view", si)}
                  onEdit={(itemIndex, item) => openEditModal("agent-installation-view", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("agent-installation-view", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("agent-installation-view", si)}
                  onDeleteSection={() => deleteSection("agent-installation-view", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("agent-installation-view")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Agent Edit Installation (Brokerage agent / Direct agent) */}
          {activeTab === "agent-installation-edit" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-edit-line" style={{ color: theme.accent }} />
                Agent Edit Installation Test Cases (Brokerage agent / Direct agent)
              </h5>
              {(sections["agent-installation-edit"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`agent-installation-edit-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("agent-installation-edit", si)}
                  onEdit={(itemIndex, item) => openEditModal("agent-installation-edit", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("agent-installation-edit", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("agent-installation-edit", si)}
                  onDeleteSection={() => deleteSection("agent-installation-edit", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("agent-installation-edit")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Agent Checkout tab */}
          {activeTab === "agent-checkout" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-shopping-bag-line" style={{ color: theme.accent }} />
                Agent Order Checkout Test Cases
              </h5>
              {(sections["agent-checkout"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`agent-checkout-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("agent-checkout", si)}
                  onEdit={(itemIndex, item) => openEditModal("agent-checkout", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("agent-checkout", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("agent-checkout", si)}
                  onDeleteSection={() => deleteSection("agent-checkout", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("agent-checkout")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Realtor Checkout tab */}
          {activeTab === "realtor-checkout" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-store-2-line" style={{ color: theme.accent }} />
                Realtor Order Checkout Test Cases
              </h5>
              {(sections["realtor-checkout"] || []).map((sec, si) => (
                <ChecklistCard
                  key={sec.sectionId ?? si}
                  theme={theme}
                  title={sec.title}
                  icon={sec.icon}
                  items={sec.items}
                  checkKeyPrefix={`realtor-checkout-${sec.sectionId ?? si}`}
                  checks={checks}
                  onToggle={onToggle}
                  onAdd={() => openAddModal("realtor-checkout", si)}
                  onEdit={(itemIndex, item) => openEditModal("realtor-checkout", si, itemIndex, item)}
                  onDelete={(itemIndex, item) => deleteTestCase("realtor-checkout", si, itemIndex, item)}
                  onEditSection={() => openEditSectionModal("realtor-checkout", si)}
                  onDeleteSection={() => deleteSection("realtor-checkout", si)}
                />
              ))}
              <button
                type="button"
                className="btn btn-sm border-0 d-flex align-items-center gap-2 mb-4"
                onClick={() => openAddSectionModal("realtor-checkout")}
                style={{
                  color: theme.accent,
                  background: theme.accentMuted,
                  borderRadius: theme.radius.sm,
                  padding: "8px 14px",
                  fontWeight: 500,
                }}
              >
                <i className="ri-add-line" /> Add new card
              </button>
            </>
          )}

          {/* Routes tab */}
          {activeTab === "routes" && (
            <>
              <h5 className="mb-4 fw-bold d-flex align-items-center gap-2" style={{ color: theme.text.primary }}>
                <i className="ri-route-line" style={{ color: theme.accent }} />
                Route Mapping (index.tsx)
              </h5>
              <div
                className="overflow-hidden rounded-3"
                style={{ background: theme.bg.card, border: `1px solid ${theme.border}` }}
              >
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <th className="py-3 border-0 small fw-semibold" style={{ color: theme.text.muted, backgroundColor: "rgba(0,0,0,0.2)" }}>Route</th>
                        <th className="py-3 border-0 small fw-semibold" style={{ color: theme.text.muted, backgroundColor: "rgba(0,0,0,0.2)" }}>Type</th>
                        <th className="py-3 border-0 small fw-semibold" style={{ color: theme.text.muted, backgroundColor: "rgba(0,0,0,0.2)" }}>Component</th>
                        <th className="py-3 border-0 small fw-semibold" style={{ color: theme.text.muted, backgroundColor: "rgba(0,0,0,0.2)" }}>Description</th>
                        <th className="py-3 border-0 text-end" style={{ width: 80, backgroundColor: "rgba(0,0,0,0.2)" }}></th>
                      </tr>
                    </thead>
                      <tbody style={{ backgroundColor: theme.bg.card }}>
                        {ROUTES_DATA.map((r) => (
                          <RouteRow key={r.path} theme={theme} path={r.path} type={r.type} component={r.component} description={r.description} />
                        ))}
                      </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </div>

      <TestCaseModal
        show={!!modal}
        mode={modal?.mode ?? "add"}
        initialId={modal?.initialId ?? ""}
        initialLabel={modal?.initialLabel ?? ""}
        onSave={saveTestCase}
        onClose={closeModal}
        theme={theme}
      />
      <SectionModal
        show={!!sectionModal}
        mode={sectionModal?.mode ?? "add"}
        initialTitle={sectionModal?.initialTitle ?? ""}
        initialIcon={sectionModal?.initialIcon ?? "ri-folder-line"}
        onSave={saveSection}
        onClose={closeSectionModal}
        theme={theme}
      />
    </div>
  );
};

export default SignageOrdersDocs;
